import { first } from 'dom-native';

import type { HelpAgentAttachmentPreview } from '@/types/attachments';
import type { ChatMessage, ConnectionStatus, GalleryState, ToastMessage } from '@/types/chat';
import type { HelpCenterFooterLinks } from '@/utils/help-center-links';

interface HelpAgentSessionInfoState {
  open: boolean;
  sessionId: string;
  status: ConnectionStatus;
}

export interface HelpAgentViewState {
  expanded: boolean;
  sessionInfo: HelpAgentSessionInfoState;
  quickPrompts: readonly string[];
  messages: readonly ChatMessage[];
  footerLinks: HelpCenterFooterLinks;
  attachments: readonly HelpAgentAttachmentPreview[];
  attachmentsEnabled: boolean;
  attachmentAccept: string;
  composerLocked: boolean;
  connectionStatus: ConnectionStatus;
  toasts: readonly ToastMessage[];
  gallery: GalleryState | null;
}

interface HelpAgentHeaderView extends Element {
  setExpanded?: (expanded: boolean) => void;
}

interface HelpAgentSessionInfoView extends Element {
  setState?: (state: HelpAgentSessionInfoState) => void;
}

interface HelpAgentMessageListView extends Element {
  quickPrompts: readonly string[];
  messages: readonly ChatMessage[];
}

interface HelpAgentComposerView extends Element {
  footerLinks: HelpCenterFooterLinks;
  attachments: readonly HelpAgentAttachmentPreview[];
  attachmentsEnabled: boolean;
  attachmentAccept: string;
  composerLocked: boolean;
  setConnectionStatus?: (status: ConnectionStatus) => void;
  focusInput?: () => void;
  clearDraft?: () => void;
}

interface HelpAgentToastStackView extends Element {
  setToasts?: (toasts: ToastMessage[]) => void;
}

interface HelpAgentGalleryModalView extends Element {
  setState?: (gallery: GalleryState | null) => void;
}

export class HelpAgentViewAdapter {
  constructor(private readonly root: HTMLElement) {}

  sync(state: HelpAgentViewState): void {
    this.root.classList.toggle('is-expanded', state.expanded);
    this.setExpanded(state.expanded);
    this.setSessionInfoState(state.sessionInfo);
    this.setQuickPrompts(state.quickPrompts);
    this.setMessages(state.messages);
    this.setFooterLinks(state.footerLinks);
    this.setAttachments(state.attachments);
    this.setAttachmentsEnabled(state.attachmentsEnabled);
    this.setAttachmentAccept(state.attachmentAccept);
    this.setComposerLocked(state.composerLocked);
    this.setConnectionStatus(state.connectionStatus);
    this.setToasts(state.toasts);
    this.setGalleryState(state.gallery);
  }

  setExpanded(expanded: boolean): void {
    this.header?.setExpanded?.(expanded);
  }

  setSessionInfoState(state: HelpAgentSessionInfoState): void {
    this.sessionInfo?.setState?.(state);
  }

  setQuickPrompts(quickPrompts: readonly string[]): void {
    if (this.messageList) {
      this.messageList.quickPrompts = quickPrompts;
    }
  }

  setMessages(messages: readonly ChatMessage[]): void {
    if (this.messageList) {
      this.messageList.messages = messages;
    }
  }

  setFooterLinks(footerLinks: HelpCenterFooterLinks): void {
    if (this.composer) {
      this.composer.footerLinks = footerLinks;
    }
  }

  setAttachments(attachments: readonly HelpAgentAttachmentPreview[]): void {
    if (this.composer) {
      this.composer.attachments = attachments;
    }
  }

  setAttachmentsEnabled(enabled: boolean): void {
    if (this.composer) {
      this.composer.attachmentsEnabled = enabled;
    }
  }

  setAttachmentAccept(accept: string): void {
    if (this.composer) {
      this.composer.attachmentAccept = accept;
    }
  }

  setComposerLocked(locked: boolean): void {
    if (this.composer) {
      this.composer.composerLocked = locked;
    }
  }

  setConnectionStatus(status: ConnectionStatus): void {
    this.composer?.setConnectionStatus?.(status);
  }

  focusComposerInput(): void {
    this.composer?.focusInput?.();
  }

  clearComposerDraft(): void {
    this.composer?.clearDraft?.();
  }

  setToasts(toasts: readonly ToastMessage[]): void {
    this.toastStack?.setToasts?.([...toasts]);
  }

  setGalleryState(gallery: GalleryState | null): void {
    this.galleryModal?.setState?.(gallery);
  }

  private get header(): HelpAgentHeaderView | null {
    return first(this.root, 'help-agent-header') as HelpAgentHeaderView | null;
  }

  private get sessionInfo(): HelpAgentSessionInfoView | null {
    return first(this.root, 'help-agent-session-info') as HelpAgentSessionInfoView | null;
  }

  private get messageList(): HelpAgentMessageListView | null {
    return first(this.root, 'help-agent-message-list') as HelpAgentMessageListView | null;
  }

  private get composer(): HelpAgentComposerView | null {
    return first(this.root, 'help-agent-composer') as HelpAgentComposerView | null;
  }

  private get toastStack(): HelpAgentToastStackView | null {
    return first(this.root, 'help-agent-toast-stack') as HelpAgentToastStackView | null;
  }

  private get galleryModal(): HelpAgentGalleryModalView | null {
    return first(this.root, 'help-agent-image-gallery-modal') as HelpAgentGalleryModalView | null;
  }
}
